<template>
  <div>
    <!-- Notificazione -->
    <Notification
      :show="notification.show"
      :type="notification.type"
      :title="notification.title"
      :message="notification.message"
      @close="notification.show = false"
    />
    
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
      <div class="flex items-center justify-between pt-6">
        <div class="flex items-center gap-4">
          <h1 class="text-2xl font-bold text-gray-900">Report odierno</h1>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-gray-600 text-sm">Aggiornamento tra: {{ remainingSeconds }} secondi</span>
          <button 
            @click="refreshData" 
            class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-sm flex items-center"
            title="Aggiorna ora"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Aggiorna ora
          </button>
        </div>
      </div>
    </div>

    <!-- Statistiche del giorno -->
    <div class="p-6">
      <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div class="bg-white rounded-lg shadow p-4">
          <div class="flex items-center">
            <div class="p-2 bg-blue-100 rounded-lg">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-gray-600">Visite Oggi</p>
              <p class="text-2xl font-semibold text-gray-900">{{ todayStats.total_visits }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-4">
          <div class="flex items-center">
            <div class="p-2 bg-green-100 rounded-lg">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-gray-600">In Azienda</p>
              <p class="text-2xl font-semibold text-gray-900">{{ todayStats.ongoing_visits }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-4">
          <div class="flex items-center">
            <div class="p-2 bg-yellow-100 rounded-lg">
              <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-gray-600">In Attesa</p>
              <p class="text-2xl font-semibold text-gray-900">{{ todayStats.pending_visits }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-4">
          <div class="flex items-center">
            <div class="p-2 bg-purple-100 rounded-lg">
              <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-gray-600">Consegne</p>
              <p class="text-2xl font-semibold text-gray-900">{{ todayStats.total_deliveries }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-4">
          <div class="flex items-center">
            <div class="p-2 bg-red-100 rounded-lg">
              <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-gray-600">Da Firmare</p>
              <p class="text-2xl font-semibold text-gray-900">{{ todayStats.pending_signatures }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-4">
          <div class="flex items-center">
            <div class="p-2 bg-orange-100 rounded-lg">
              <svg class="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"></path>
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-gray-600">Assistenza</p>
              <p class="text-2xl font-semibold text-gray-900">{{ todayStats.pending_assistance }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabella completa degli ingressi di oggi -->
      <div class="bg-white rounded-lg shadow">
        <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 class="text-lg font-medium text-gray-900 flex items-center">
            <svg class="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
            </svg>
            Registro Ingressi di Oggi
          </h3>
          <Link 
            :href="route('modules.rd-manage-entries')"
            class="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center"
          >
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"></path>
            </svg>
            Sblocco accessi
          </Link>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Azienda/Destinazione</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referente</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingresso</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uscita</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <!-- Visitatori -->
              <tr v-for="visit in allVisitsToday" :key="'visit-' + visit.id" class="hover:bg-gray-50">
                <td class="px-6 py-4">
                  <div>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                      </svg>
                      Visitatore
                    </span>
                    <div class="mt-1">
                      <div class="text-sm font-medium text-gray-900">{{ visit.visitor_name }}</div>
                      <div class="text-sm text-gray-500">Badge: {{ visit.badge?.code || 'N/A' }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-900">{{ visit.visitor_company }}</div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-900">{{ visit.contact?.name || 'Nessuno' }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatDateTime(visit.check_in_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ visit.check_out_at ? formatDateTime(visit.check_out_at) : '-' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span v-if="!visit.confirmed" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    In Attesa
                  </span>
                  <span v-else-if="!visit.check_out_at" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    In Azienda
                  </span>
                  <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Uscito
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div v-if="!visit.confirmed" class="flex gap-1">
                    <button 
                      @click="confirmVisit(visit.id)"
                      class="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs"
                      title="Conferma visita"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </button>
                    <button 
                      @click="rejectVisit(visit.id)"
                      class="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs"
                      title="Rifiuta visita"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </div>
                  <div v-else-if="!visit.check_out_at" class="flex gap-1">
                    <button 
                      @click="forceClose(visit.id)"
                      class="bg-orange-500 hover:bg-orange-600 text-white px-2 py-1 rounded text-xs"
                      title="Forza uscita"
                    >
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>

              <!-- Consegne -->
              <tr v-for="delivery in allDeliveriesToday" :key="'delivery-' + delivery.id" class="hover:bg-gray-50">
                <td class="px-6 py-4">
                  <div>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" 
                          :class="delivery.delivery_type === 'con-firma' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'">
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                      </svg>
                      {{ delivery.delivery_type === 'con-firma' ? 'Con Firma' : 'Senza Firma' }}
                    </span>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-900">{{ delivery.recipient }}</div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-900"></div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatDateTime(delivery.created_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ (delivery.deposited || delivery.confirmed) ? formatDateTime(delivery.updated_at) : '-' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span v-if="delivery.delivery_type === 'con-firma' && !delivery.confirmed"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    In Attesa Firma
                  </span>
                  <span v-else-if="delivery.delivery_type === 'senza-firma' && delivery.deposited"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Depositato
                  </span>
                  <span v-else-if="delivery.confirmed"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Confermato
                  </span>
                  <span v-else 
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    In Elaborazione
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div v-if="delivery.delivery_type === 'con-firma' && !delivery.confirmed" class="flex gap-1">
                    <button 
                      @click="confirmDelivery(delivery.id)"
                      class="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs"
                      title="Conferma firma"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4"></path>
                      </svg>
                    </button>
                  </div>
                  <div v-else-if="delivery.delivery_type === 'senza-firma' && !delivery.deposited" class="flex gap-1">
                    <button 
                      @click="depositDelivery(delivery.id)"
                      class="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
                      title="Marca come depositato"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>

              <!-- Richieste di assistenza -->
              <tr v-for="request in allAssistanceToday" :key="'assistance-' + request.id" class="hover:bg-gray-50">
                <td class="px-6 py-4">
                  <div>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"></path>
                      </svg>
                      Richiesta Assistenza
                    </span>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-900">-</div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-900">-</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatDateTime(request.created_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ request.confirmed ? formatDateTime(request.updated_at) : '-' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span v-if="!request.confirmed" 
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    In Attesa
                  </span>
                  <span v-else 
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Gestita
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div v-if="!request.confirmed" class="flex gap-1">
                    <button 
                      @click="confirmAssistance(request.id)"
                      class="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs"
                      title="Prendi in carico"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>

              <!-- Messaggio quando non ci sono ingressi -->
              <tr v-if="!hasAnyEntriesToday">
                <td colspan="7" class="px-6 py-12 text-center">
                  <svg class="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <p class="text-gray-500">Nessun ingresso registrato oggi</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Tabella ingressi giorni precedenti -->
      <div class="bg-white rounded-lg shadow mt-4" v-if="pastUnclosedVisits.length > 0">
        <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 class="text-lg font-medium text-gray-900 flex items-center">
            <svg class="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
            </svg>
            Registro visite rimaste aperte da giorni precedenti
          </h3>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Azienda/Destinazione</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referente</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingresso</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uscita</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <!-- Visitatori -->
              <tr v-for="visit in pastUnclosedVisits" :key="'visit-' + visit.id" class="hover:bg-gray-50">
                <td class="px-6 py-4">
                  <div>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                      </svg>
                      Visitatore
                    </span>
                    <div class="mt-1">
                      <div class="text-sm font-medium text-gray-900">{{ visit.visitor_name }}</div>
                      <div class="text-sm text-gray-500">Badge: {{ visit.badge?.code || 'N/A' }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-900">{{ visit.visitor_company }}</div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-900">{{ visit.contact?.name || 'Nessuno' }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatDateTime(visit.check_in_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  -
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    In Azienda
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div v-if="!visit.check_out_at" class="flex gap-1">
                    <button 
                      @click="forceClose(visit.id)"
                      class="bg-orange-500 hover:bg-orange-600 text-white px-2 py-1 rounded text-xs"
                      title="Forza uscita"
                    >
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Notification from '@/Components/Notification.vue';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

export default {
  props: [
    'allVisitsToday', 
    'allDeliveriesToday', 
    'allAssistanceToday', 
    'todayStats',
    'pastUnclosedVisits',
    'companies'
  ],
  components: {
    Link,
    BreadCrumbs,
    Notification
  },
  data() {
    return {
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: this.$t('modules.reception-digitale'), href: route('modules.home', 'reception-digitale') },
        { name: 'Report odierno', href: route('modules.rd-dashboard') }
      ],
      refreshInterval: 30,
      remainingSeconds: 30,
      timerId: null,
      notification: {
        show: false,
        type: 'success',
        title: '',
        message: ''
      }
    };
  },
  computed: {
    hasAnyEntriesToday() {
      return (this.allVisitsToday && this.allVisitsToday.length > 0) ||
             (this.allDeliveriesToday && this.allDeliveriesToday.length > 0) ||
             (this.allAssistanceToday && this.allAssistanceToday.length > 0);
    }
  },
  mounted() {
    this.startRefreshTimer();
  },
  beforeUnmount() {
    this.clearRefreshTimer();
  },
  methods: {
    formatDateTime(dateString) {
      if (!dateString) return '';
      try {
        return format(parseISO(dateString), 'dd/MM/yyyy HH:mm', { locale: it });
      } catch (error) {
        return dateString;
      }
    },
    startRefreshTimer() {
      this.clearRefreshTimer();
      this.remainingSeconds = this.refreshInterval;
      
      this.timerId = setInterval(() => {
        this.remainingSeconds--;
        if (this.remainingSeconds <= 0) {
          this.refreshData();
        }
      }, 1000);
    },
    clearRefreshTimer() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    },
    refreshData() {
      router.reload({ 
        preserveScroll: true, 
        onSuccess: () => {
          this.startRefreshTimer();
        }
      });
    },
    showNotification(type, title, message) {
      this.notification = {
        show: true,
        type,
        title,
        message
      };
    },
    confirmVisit(visitId) {
      router.post(route('modules.rd-conferma-visita', visitId), {}, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          this.showNotification('success', 'Visita Confermata', 'La visita è stata confermata con successo.');
          this.startRefreshTimer();
        },
        onError: () => {
          this.showNotification('error', 'Errore', 'Si è verificato un errore durante la conferma della visita.');
        }
      });
    },
    rejectVisit(visitId) {
      router.post(route('modules.rd-rifiuta-visita', visitId), {}, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          this.showNotification('success', 'Visita Rifiutata', 'La visita è stata rifiutata.');
          this.startRefreshTimer();
        },
        onError: () => {
          this.showNotification('error', 'Errore', 'Si è verificato un errore durante il rifiuto della visita.');
        }
      });
    },
    forceClose(visitId) {
      router.post(route('modules.rd-uscita-forzata', visitId), {}, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          this.showNotification('success', 'Chiusura forzata visita', 'La visita è stata chiusa forzatamente con successo.');
          this.startRefreshTimer();
        },
        onError: () => {
          this.showNotification('error', 'Errore', 'Si è verificato un errore durante la chiusura forzata della visita.');
        }
      });
    },
    confirmDelivery(deliveryId) {
      router.post(route('modules.rd-conferma-consegna', deliveryId), {}, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          this.showNotification('success', 'Consegna Confermata', 'La consegna è stata confermata con successo.');
          this.startRefreshTimer();
        },
        onError: () => {
          this.showNotification('error', 'Errore', 'Si è verificato un errore durante la conferma della consegna.');
        }
      });
    },
    depositDelivery(deliveryId) {
      router.post(route('modules.rd-deposit-delivery', deliveryId), {}, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          this.showNotification('success', 'Consegna Depositata', 'La consegna è stata marcata come depositata.');
          this.startRefreshTimer();
        },
        onError: () => {
          this.showNotification('error', 'Errore', 'Si è verificato un errore durante il deposito della consegna.');
        }
      });
    },
    confirmAssistance(requestId) {
      router.post(route('modules.rd-conferma-assistenza', requestId), {}, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          this.showNotification('success', 'Assistenza Gestita', 'La richiesta di assistenza è stata presa in carico.');
          this.startRefreshTimer();
        },
        onError: () => {
          this.showNotification('error', 'Errore', 'Si è verificato un errore durante la gestione della richiesta di assistenza.');
        }
      });
    }
  }
};
</script>

<style>
.notification-enter-active, .notification-leave-active {
  transition: all 0.3s ease;
}
.notification-enter-from, .notification-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
